/**
 * The day in numbers, for the Live summary and each container's header
 * (20/10/2026): drops and SMS sent of planned, the estimated spend, the
 * next drop and the last one sent, and what needs a look (due and not
 * sent, held, left for a person, stopped). A drop skipped by hand is no longer planned. Pure.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DayPlanContainerRow, DayPlanDropRow } from '../../../drop-generator.types';
import { dropState, type DropState, isOpen } from '../strip/drop-state';

export interface DropRef {
  atUtc: string;
  container: string;
}

export interface LiveTotals {
  drops: { sent: number; planned: number };
  sms: { sent: number; planned: number };
  /** Estimated US dollars — null when a price is unknown. */
  cost: { spent: number | null; planned: number | null };
  next: DropRef | null;
  last: DropRef | null;
  overdue: number;
  held: number;
  /** Auto-send gave these up — a person sends them. */
  left: number;
  stopped: number;
}

type Row = { drop: DayPlanDropRow; state: DropState; container: string };

const sum = (rows: Row[], pick: (d: DayPlanDropRow) => number | null): number | null =>
  rows.reduce<number | null>((n, r) => (n === null || pick(r.drop) === null ? null : n + (pick(r.drop) as number)), 0);

const earliest = (rows: Row[], at: (r: Row) => string): DropRef | null => {
  const first = [...rows].sort((a, b) => at(a).localeCompare(at(b)))[0];
  return first ? { atUtc: at(first), container: first.container } : null;
};

export function liveTotals(containers: DayPlanContainerRow[], now: Date): LiveTotals {
  const rows: Row[] = containers.flatMap((c) => c.drops.map((drop) => ({ drop, state: dropState(drop, now), container: c.name })));
  const planned = rows.filter((r) => r.state !== 'skipped' && r.state !== 'stopped');
  const sent = rows.filter((r) => r.state === 'sent');
  const upcoming = planned.filter((r) => isOpen(r.state) && r.state !== 'overdue' && new Date(r.drop.atUtc) >= now);
  const lastSent = [...sent].sort((a, b) => (b.drop.runtime?.sentAt ?? '').localeCompare(a.drop.runtime?.sentAt ?? ''))[0];
  return {
    drops: { sent: sent.length, planned: planned.length },
    sms: { sent: sum(sent, (d) => d.amount) ?? 0, planned: sum(planned, (d) => d.amount) ?? 0 },
    cost: { spent: sum(sent, (d) => d.estCost), planned: sum(planned, (d) => d.estCost) },
    next: earliest(upcoming, (r) => r.drop.atUtc),
    last: lastSent ? { atUtc: lastSent.drop.runtime?.sentAt as string, container: lastSent.container } : null,
    overdue: rows.filter((r) => r.state === 'overdue').length,
    held: rows.filter((r) => r.state === 'held').length,
    left: rows.filter((r) => r.state === 'left').length,
    stopped: rows.filter((r) => r.state === 'stopped').length,
  };
}
